import React, {useEffect} from 'react';
import {
    IonHeader,
    IonFooter,
    IonToolbar,
    IonTitle,
    IonButton,
    IonContent,
    IonGrid,
    IonRow,
    IonCol,
    IonInput,
    IonList,
    IonText,
    IonLabel,
    IonItem,
    IonListHeader,
    IonCard,
    IonRadio,
    IonRadioGroup,
    IonDatetime,
    IonSelect,
    IonSelectOption,
    IonLoading,
    IonAlert
} from '@ionic/react';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import './CreateReport.scss';
import get from 'lodash/get';
import map from 'lodash/map';
import find from 'lodash/find';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {getReasonCategories} from "../../../../actions/reason";
import moment from 'moment';
import {resetConsigneeReport, STORE_CONSIGNEE_REPORT, storeConsigneeReport} from "../../../../actions/consignee";

/**
 * Create consignee delivery report modal form
 * @param order
 * @param onCancel
 * @param onSuccess
 * @param getReasons
 * @param category
 * @param report
 * @param goods_statuses
 * @param storeReport
 * @param reInit
 * @returns {JSX.Element}
 * @constructor
 */
const CreateReport = ({order, onCancel, onSuccess, getReasons, category, report, goods_statuses, storeReport, reInit, showUnloadOnly}) => {
    useEffect(() => {
        reInit(); // reset consignee.store state to initial value
        if (get(category, 'data.rows.length', 0) === 0) {
            // only get reasons categories if not stored on redux
            getReasons(query);
        }
    }, []);

    const query = {
        populate: 'reasons',
    }
    const minimumDate = moment().format();
    const displayFormat = 'DD MMM YYYY';

    /**
     * Form initial values
     * @type {{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}}
     */
    const initialValues = {
        order_id: get(order, 'id'),
        goods_status: showUnloadOnly ? 'unload' : 'issue',
        classification_id: '',
        reason_id: '',
        dropoff_at: '',
        unload_at: '',
        remarks: '',
    }

    /**
     * Form validation schema
     * @type {ObjectSchema|undefined}
     */
    let validationSchema = Yup.object({
        order_id: Yup.string()
            .required('Order id is required'),
        goods_status: Yup.string()
            .required('Harap pilih kualifikasi status barang'),
        classification_id: Yup.string()
            .when('goods_status', {
                is: (value) => value === 'issue',
                then: Yup.string().required('Harap pilih klasifikasi.'),
            }),
        reason_id: Yup.string()
            .when('goods_status', {
                is: (value) => value === 'issue',
                then: Yup.string().required('Harap pilih alasan.'),
            }),
        unload_at: Yup.string()
            .required('Harap isi waktu unload.'),
        dropoff_at: Yup.string()
            .when('goods_status', {
                is: 'issue',
                then: Yup.string().required('Harap pilih estimasi waktu kedatangan.')
            }),
        remarks: Yup.string()
            .when('goods_status', {
                is: 'issue',
                then: Yup.string().required('Remarks can not be empty')
            }),
    });

    /**
     * Handle on submit form
     * @param data
     */
    const onSubmit = (data) => {
        delete data.classification_id;

        if (data.dropoff_at) {
            data.dropoff_at = moment(data.dropoff_at).format('YYYY-MM-DD');
        }
        if (data.unload_at) {
            data.unload_at = moment(data.unload_at).format('YYYY-MM-DD');
        }

        let body = data;

        if (data.goods_status === 'unload') {
            body = {
                order_id: data.order_id,
                goods_status: data.goods_status,
                unload_at: data.unload_at,
            }
        }

        storeReport(body).then(res => {
            if (get(res, 'type') === `${STORE_CONSIGNEE_REPORT}_SUCCESS` && !get(res, 'payload.data.errorMessage') && onSuccess) {
                onSuccess();
            }
        });
    }

    /**
     * Define formik to handle form & validation
     * @type {{initialValues: {goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}, initialErrors: FormikErrors<unknown>, initialTouched: FormikTouched<unknown>, initialStatus: any, handleBlur: {(e: React.FocusEvent<any>): void, <T=any>(fieldOrEvent: T): T extends string ? ((e: any) => void) : void}, handleChange: {(e: React.ChangeEvent<any>): void, <T_1=string | React.ChangeEvent<any>>(field: T_1): T_1 extends React.ChangeEvent<any> ? void : ((e: (string | React.ChangeEvent<any>)) => void)}, handleReset: (e: any) => void, handleSubmit: (e?: (React.FormEvent<HTMLFormElement> | undefined)) => void, resetForm: (nextState?: (Partial<FormikState<{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}>> | undefined)) => void, setErrors: (errors: FormikErrors<{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}>) => void, setFormikState: (stateOrCb: (FormikState<{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}> | ((state: FormikState<{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}>) => FormikState<{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}>))) => void, setFieldTouched: (field: string, touched?: boolean, shouldValidate?: (boolean | undefined)) => any, setFieldValue: (field: string, value: any, shouldValidate?: (boolean | undefined)) => any, setFieldError: (field: string, value: (string | undefined)) => void, setStatus: (status: any) => void, setSubmitting: (isSubmitting: boolean) => void, setTouched: (touched: FormikTouched<{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}>, shouldValidate?: (boolean | undefined)) => any, setValues: (values: {goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}, shouldValidate?: (boolean | undefined)) => any, submitForm: () => Promise<any>, validateForm: (values?: {goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}) => Promise<FormikErrors<{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}>>, validateField: (name: string) => (Promise<void> | Promise<string | undefined>), isValid: boolean, dirty: boolean, unregisterField: (name: string) => void, registerField: (name: string, {validate}: any) => void, getFieldProps: (nameOrOptions: any) => FieldInputProps<any>, getFieldMeta: (name: string) => FieldMetaProps<any>, getFieldHelpers: (name: string) => FieldHelperProps<any>, validateOnBlur: boolean, validateOnChange: boolean, validateOnMount: boolean, values: {goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}, errors: FormikErrors<{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}>, touched: FormikTouched<{goods_status: string, classification_id: string, unload_at: string, order_id, remarks: string, reason_id: string, dropoff_at: string}>, isSubmitting: boolean, isValidating: boolean, status?: any, submitCount: number}}
     */
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
    });

    /**
     * Error text component, to display error of field
     * @param name
     * @returns {JSX.Element|null}
     * @constructor
     */
    const ErrorText = ({name}) => get(formik, `touched[${name}]`) && get(formik, `errors[${name}]`, '') ? (
        <div style={{paddingLeft: 16}}>
            <IonText color={'danger'}>
                <small>{get(formik, `errors[${name}]`, '')}</small>
            </IonText>
        </div>) : null;

    /**
     * filter reasons by selected classification_id
     */
    const reasons = () => {
        const item = find(get(category, 'data', []), {id: get(formik, 'values.classification_id', null)});

        return get(item, 'reasons', get(item, 'reason', []));
    };

    const onChangeClassification = (e) => {
        formik.handleChange(e);
        formik.setFieldValue('reason_id', '');

    }

    return (
        <>
            {/* Start loading spinner */}
            <IonLoading
                cssClass='my-custom-class'
                isOpen={get(category, 'loading', false) || get(report, 'loading')}
                // onDidDismiss={() => setShowLoading(false)}
                message={'Please wait...'}
            />
            {/* End loading spinner */}

            {/* Start alert */}
            <IonAlert
                isOpen={get(report, 'error') && !get(report, 'loading')}
                cssClass='my-custom-class'
                header={'Failed'}
                subHeader={'An error occurred while saving the data'}
                message={get(report, 'error.errorMessage', '')}
                buttons={['Ok']}
            />
            {/* End alert */}

            {/* Start IonHeader */}
            <IonHeader translucent>
                <IonToolbar>
                    <IonTitle className="modal-title">
                        Delivery Tracking
                    </IonTitle>
                </IonToolbar>
            </IonHeader>
            {/* End IonHeader */}

            {/* Start IonContent */}
            <IonContent>
                <div className={'create-report'} style={{padding: 16}}>
                    {!get(category, 'loading') && get(category, 'error') && <div style={{textAlign: 'center'}}>
                        <h4>Terjadi kesalahan saat mengambil data penting!</h4>
                        <IonButton onClick={() => getReasons(query)}>Coba lagi</IonButton>
                        {get(category, 'error.errorMessage', '') && <IonCard color={'danger'}>
                            {get(category, 'error.errorMessage', '')}
                        </IonCard>}
                    </div>}
                    {!get(category, 'error') && <IonList>
                        <IonListHeader>
                            <IonLabel>Goods Status</IonLabel>
                        </IonListHeader>

                        <IonRadioGroup
                            value={get(formik, 'values.goods_status')}
                            name={'goods_status'}
                            onIonChange={formik.handleChange}
                        >
                        {showUnloadOnly ?
                            <IonItem>
                                <IonRadio slot="start" value={'unload'}/>
                                <IonLabel style={{textTransform: 'capitalize'}}>unload</IonLabel>
                            </IonItem>
                        : goods_statuses.map((status, key) => 
                            <IonItem key={key}>
                                <IonRadio slot="start" value={status}/>
                                <IonLabel style={{textTransform: 'capitalize'}}>{status}</IonLabel>
                            </IonItem>
                        )}
                        </IonRadioGroup>

                        {get(formik, 'values.goods_status', '') === 'issue' && <>
                            <IonItem>
                                <IonLabel>Klasifikasi</IonLabel>
                                <IonSelect value={get(formik, 'values.classification_id', '')} name="classification_id"
                                           placeholder="Pilih" onIonChange={onChangeClassification}>
                                    {map(get(category, 'data', []), (item, key) => <IonSelectOption key={key} value={get(item, 'id')}>{get(item, 'name')}</IonSelectOption>)}
                                </IonSelect>
                            </IonItem>
                            <ErrorText name={'classification_id'}/>
                            <IonItem>
                                <IonLabel>Detail alasan</IonLabel>
                                <IonSelect disabled={!get(formik, 'values.classification_id')}
                                           value={get(formik, 'values.reason_id')} name="reason_id"
                                           placeholder="Pilih" onIonChange={formik.handleChange}>
                                    {reasons().map((item, key) => <IonSelectOption key={key} class="ion-text-nowrap"
                                                                                   value={get(item, 'id')}>{get(item, 'description', '')}</IonSelectOption>)}
                                </IonSelect>
                            </IonItem>
                            <ErrorText name={'reason_id'}/>
                            <IonItem>
                                <IonLabel>Perkiraan tanggal kedatangan</IonLabel>
                                <IonDatetime name="dropoff_at" displayFormat={displayFormat} min={minimumDate}
                                             placeholder="Pilih tanggal" value={get(formik, 'values.dropoff_at')}
                                             onIonChange={formik.handleChange}></IonDatetime>
                            </IonItem>
                            <ErrorText name={'dropoff_at'}/>
                        </>}

                        <IonItem>
                            <IonLabel>{get(formik, 'values.goods_status', '') === 'issue' ? 'Perkiraan tanggal unload' : 'Tanggal unload'}</IonLabel>
                            <IonDatetime name="unload_at" displayFormat={displayFormat} min={get(formik, 'values.goods_status', '') === 'issue' ? minimumDate : undefined} max={get(formik, 'values.goods_status', '') !== 'issue' ? minimumDate : undefined}
                                         placeholder="Pilih tanggal" value={get(formik, 'values.unload_at')}
                                         onIonChange={formik.handleChange}></IonDatetime>
                        </IonItem>
                        <ErrorText name={'unload_at'}/>
                        {get(formik, 'values.goods_status', '') === 'issue' && <>
                            <IonItem>
                                <IonInput placeholder={'Remarks'} type={'text'} name={'remarks'}
                                          value={get(formik, 'values.remarks', '')}
                                          onIonChange={formik.handleChange}/>
                            </IonItem>
                            <ErrorText name={'remarks'}/>
                        </>
                        }
                    </IonList>}
                </div>
            </IonContent>
            {/* end IonContent */}

            {/* start IonFooter */}
            <IonFooter>
                <IonToolbar>
                    <IonGrid>
                        <IonRow>
                            <IonCol>
                                <IonButton color="light" expand="block" onClick={onCancel}>
                                    Batal
                                </IonButton>
                            </IonCol>
                            <IonCol>
                                <IonButton
                                    disabled={get(category, 'loading') || get(report, 'loading')}
                                    expand="block" onClick={formik.handleSubmit}>
                                    Simpan
                                </IonButton>
                            </IonCol>
                        </IonRow>
                    </IonGrid>
                </IonToolbar>
            </IonFooter>
            {/* end IonFooter */}
        </>
    )
}

/**
 * Map important redux state to component props
 * @param category
 * @param goods_statuses
 * @param report
 * @returns {{report: *, category: *, goods_statuses: *}}
 */
const mapStateToProps = ({reason: {category}, consignee: {goods_statuses, store: report}}) => {
    return ({
        category: category.list,
        goods_statuses,
        report,
    })
};

/**
 * Map important action to props
 * @param dispatch
 * @returns {{getReasons: (function(*=): {payload: {request: {method: string, params: *, url: string}}, type: string}), storeReport: (function(*): {payload: {request: {data: *, method: string, url: string}}, type: string}), reInit: (function(): {type: string})}}
 */
const mapDispatchToProps = (dispatch) => bindActionCreators({
    getReasons: getReasonCategories,
    storeReport: storeConsigneeReport,
    reInit: resetConsigneeReport,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CreateReport);
